import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../platform/firebase/clients';
import { trackPendingOperation } from './pendingOperations';

export interface AdjustCampaignTasksRequest {
  campaignId: string;
  taskTypeId: string;
  targetCount: number;
  domainId?: string | null;
  domainLabel?: string | null;
}

export interface AdjustCampaignTasksResult {
  schemaVersion?: number;
  dryRun?: boolean;
  created: number;
  assigned?: number;
  deleted: number;
  totalAfter: number;
  availableAfter: number;
}

export async function adjustCampaignTasks(
  req: AdjustCampaignTasksRequest,
): Promise<AdjustCampaignTasksResult> {
  const fn = httpsCallable<AdjustCampaignTasksRequest, AdjustCampaignTasksResult>(
    functions,
    'adjustCampaignTasks',
  );
  const res = await fn(req);
  return res.data;
}

export interface AssignTaskToExpertRequest {
  campaignId: string;
  taskTypeId: string;
  targetUid: string;
  domainId?: string | null;
  domainLabel?: string | null;
  /** When set, assign this existing available task. When omitted, create a
   *  fresh blank task in the domain and assign it. */
  taskId?: string | null;
}

export interface AssignTaskToExpertResult {
  taskId: string;
  created: boolean;
  ownerName: string;
}

/**
 * Admin-only: assign a task to an expert as though they had claimed it —
 * sets the owner fields to `claimed` and writes the matching taskTypeClaims
 * tracker. Runs server-side (admin SDK) because the claim tracker rules
 * require `userId == uid()`, which a client writing on another user's behalf
 * can't satisfy.
 */
export async function assignTaskToExpert(
  req: AssignTaskToExpertRequest,
): Promise<AssignTaskToExpertResult> {
  const fn = httpsCallable<AssignTaskToExpertRequest, AssignTaskToExpertResult>(
    functions,
    'assignTaskToExpert',
  );
  const res = await fn(req);
  return res.data;
}

export interface UploadTaskRow {
  taskTitle: string;
  payload: Record<string, unknown>;
  uploadKey?: string;
}

export interface UploadTasksRequest {
  campaignId: string;
  taskTypeId: string;
  rows: UploadTaskRow[];
}

export interface UploadTasksBatchResult {
  created: number;
  updated: number;
  skipped: { index: number; uploadKey: string | null; reason: string }[];
  totalAfter: number;
}

export interface UploadTasksAggregateResult {
  created: number;
  updated: number;
  skipped: { index: number; uploadKey: string | null; reason: string }[];
  totalAfter: number;
  batchCount: number;
}

/** Hard cap on the per-callable payload size. Must match
 *  `MAX_ROWS_PER_CALL` in `functions/uploadTasksFromPayloads.js`. */
export const UPLOAD_BATCH_SIZE = 250;

/**
 * Splits the incoming rows into batches of UPLOAD_BATCH_SIZE, invokes the
 * callable sequentially per batch, and aggregates the results. `onBatch`
 * fires after each batch so the UI can render progress.
 */
export function uploadTasksFromPayloads(
  req: UploadTasksRequest,
  onBatch?: (done: number, total: number) => void,
): Promise<UploadTasksAggregateResult> {
  return trackPendingOperation(uploadTasksFromPayloadsInternal(req, onBatch));
}

async function uploadTasksFromPayloadsInternal(
  req: UploadTasksRequest,
  onBatch?: (done: number, total: number) => void,
): Promise<UploadTasksAggregateResult> {
  const fn = httpsCallable<
    { campaignId: string; taskTypeId: string; rows: UploadTaskRow[] },
    UploadTasksBatchResult
  >(functions, 'uploadTasksFromPayloads');

  const batches: UploadTaskRow[][] = [];
  for (let i = 0; i < req.rows.length; i += UPLOAD_BATCH_SIZE) {
    batches.push(req.rows.slice(i, i + UPLOAD_BATCH_SIZE));
  }

  const agg: UploadTasksAggregateResult = {
    created: 0,
    updated: 0,
    skipped: [],
    totalAfter: 0,
    batchCount: batches.length,
  };

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const res = await fn({
      campaignId: req.campaignId,
      taskTypeId: req.taskTypeId,
      rows: batch,
    });
    agg.created += res.data.created;
    agg.updated += res.data.updated;
    agg.totalAfter = res.data.totalAfter;
    const base = i * UPLOAD_BATCH_SIZE;
    for (const s of res.data.skipped) {
      agg.skipped.push({ ...s, index: s.index + base });
    }
    onBatch?.(i + 1, batches.length);
  }

  return agg;
}

export interface CampaignTaskDomainInput {
  domainId: string;
  label: string;
  targetCount: number;
  claimingPaused?: boolean;
}

export async function setCampaignRequireFirstApprovalBeforeMoreClaims(
  campaignId: string,
  enabled: boolean,
): Promise<void> {
  await updateDoc(doc(db, 'campaigns', campaignId), {
    requireFirstApprovalBeforeMoreClaims: enabled,
  });
}

export async function setCampaignEscalationAdmin(
  campaignId: string,
  escalationAdminId: string | null,
): Promise<void> {
  const cleaned = escalationAdminId?.trim() || null;
  await updateDoc(doc(db, 'campaigns', campaignId), {
    escalationAdminId: cleaned,
  });
}

export async function setCampaignPauseAllowedTaskIds(campaignId: string, taskIds: string[]): Promise<void> {
  await updateDoc(doc(db, 'campaigns', campaignId), {
    pauseAllowedTaskIds: Array.from(new Set(taskIds.map((id) => id.trim()).filter(Boolean))),
  });
}

export async function setCampaignPaused(campaignId: string, paused: boolean): Promise<void> {
  await updateDoc(doc(db, 'campaigns', campaignId), { paused });
}

export async function setCampaignAutoApprove(
  campaignId: string,
  enabled: boolean,
): Promise<void> {
  await updateDoc(doc(db, 'campaigns', campaignId), {
    autoApprove: { enabled },
  });
}

export async function setCampaignSubmissionEmail(
  campaignId: string,
  enabled: boolean,
): Promise<void> {
  await updateDoc(doc(db, 'campaigns', campaignId), {
    submissionEmail: { enabled },
  });
}

export const AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS = 1;
export const AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS = 365;
export const MAX_TASKS_PER_USER_LIMIT = 1000;
export const MAX_CLAIMED_TASKS_PER_USER_LIMIT = 1000;

export async function setCampaignAutoReleaseStaleClaims(
  campaignId: string,
  enabled: boolean,
  days: number,
): Promise<void> {
  const cleanedDays = Math.trunc(days);
  if (
    !Number.isInteger(cleanedDays)
    || cleanedDays < AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS
    || cleanedDays > AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS
  ) {
    throw new Error(
      `days must be an integer in [${AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS}, ${AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS}].`,
    );
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    autoReleaseStaleClaims: { enabled, days: cleanedDays },
  });
}

export const AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS = AUTO_RELEASE_STALE_CLAIMS_MIN_DAYS;
export const AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS = AUTO_RELEASE_STALE_CLAIMS_MAX_DAYS;

export async function setCampaignAutoReleaseStaleInProgress(
  campaignId: string,
  enabled: boolean,
  days: number,
): Promise<void> {
  const cleanedDays = Math.trunc(days);
  if (
    !Number.isInteger(cleanedDays)
    || cleanedDays < AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS
    || cleanedDays > AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS
  ) {
    throw new Error(
      `days must be an integer in [${AUTO_RELEASE_STALE_IN_PROGRESS_MIN_DAYS}, ${AUTO_RELEASE_STALE_IN_PROGRESS_MAX_DAYS}].`,
    );
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    autoReleaseStaleInProgress: { enabled, days: cleanedDays },
  });
}

export const AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS = 1;
export const AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS = 8760;

export async function setCampaignAutoReleaseStaleReviews(
  campaignId: string,
  enabled: boolean,
  hours: number,
): Promise<void> {
  const cleanedHours = Math.trunc(hours);
  if (
    !Number.isInteger(cleanedHours)
    || cleanedHours < AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS
    || cleanedHours > AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS
  ) {
    throw new Error(
      `hours must be an integer in [${AUTO_RELEASE_STALE_REVIEWS_MIN_HOURS}, ${AUTO_RELEASE_STALE_REVIEWS_MAX_HOURS}].`,
    );
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    autoReleaseStaleReviews: { enabled, hours: cleanedHours },
  });
}

export async function setCampaignMaxTasksPerUser(
  campaignId: string,
  maxTasksPerUser: number | null,
): Promise<void> {
  if (maxTasksPerUser == null) {
    await updateDoc(doc(db, 'campaigns', campaignId), {
      maxTasksPerUser: null,
    });
    return;
  }
  const cleaned = Math.trunc(maxTasksPerUser);
  if (!Number.isInteger(cleaned) || cleaned < 1 || cleaned > MAX_TASKS_PER_USER_LIMIT) {
    throw new Error(`maxTasksPerUser must be an integer in [1, ${MAX_TASKS_PER_USER_LIMIT}].`);
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    maxTasksPerUser: cleaned,
  });
}

export async function setCampaignMaxTasksPerTrustedUser(
  campaignId: string,
  maxTasksPerTrustedUser: number | null,
): Promise<void> {
  if (maxTasksPerTrustedUser == null) {
    await updateDoc(doc(db, 'campaigns', campaignId), {
      maxTasksPerTrustedUser: null,
    });
    return;
  }
  const cleaned = Math.trunc(maxTasksPerTrustedUser);
  if (!Number.isInteger(cleaned) || cleaned < 1 || cleaned > MAX_TASKS_PER_USER_LIMIT) {
    throw new Error(`maxTasksPerTrustedUser must be an integer in [1, ${MAX_TASKS_PER_USER_LIMIT}].`);
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    maxTasksPerTrustedUser: cleaned,
  });
}

export async function setCampaignMaxClaimedTasksPerUser(
  campaignId: string,
  maxClaimedTasksPerUser: number | null,
): Promise<void> {
  if (maxClaimedTasksPerUser == null) {
    await updateDoc(doc(db, 'campaigns', campaignId), {
      maxClaimedTasksPerUser: null,
    });
    return;
  }
  const cleaned = Math.trunc(maxClaimedTasksPerUser);
  if (
    !Number.isInteger(cleaned)
    || cleaned < 1
    || cleaned > MAX_CLAIMED_TASKS_PER_USER_LIMIT
  ) {
    throw new Error(
      `maxClaimedTasksPerUser must be an integer in [1, ${MAX_CLAIMED_TASKS_PER_USER_LIMIT}].`,
    );
  }
  await updateDoc(doc(db, 'campaigns', campaignId), {
    maxClaimedTasksPerUser: cleaned,
  });
}
